import { useState } from "react";
import axios from "axios";
import { AlertTriangle, Mail } from "lucide-react";
import { Card, Button } from "../../../components";
import EmailCodeStep from "../../../components/EmailCodeStep/EmailCodeStep";
import {
  PROFILE_EMAIL_CODE_URL,
  PROFILE_EMAIL_CONFIRM_URL,
} from "../../../features/UrlHelper";

/**
 * Doplnenie alebo zmena e-mailu vo vlastnom profile (R25).
 *
 * PRECO JE TO TU TAK NA OCIACH:
 *   Preflight ukazal, ze v ostrej databaze nema e-mail NIKTO -- ani obsluha.
 *   Obnova hesla e-mailom je teda pre celu prevadzku nepouzitelna, kym si ho
 *   ludia nedoplnia. Preto je karta bez e-mailu vyrazne oznacena; s e-mailom
 *   je to uz obycajne pole s tlacidlom na zmenu.
 *
 * Kod chodi na NOVU adresu a az potom sa zapise -- inak by sa cudzim ockom
 * pri odomknutom pocitaci dal ucet natrvalo prepisat na vlastny e-mail.
 *
 * Props:
 *  - email      {string} sucasny e-mail (prazdny = nie je)
 *  - onZmenene  {fn}     (novyEmail) => void, nech si rodic obnovi udaje
 */
function EmailKarta({ email, onZmenene }) {
  // "zavrete" -> "adresa" (zadava sa e-mail) -> "kod" (overuje sa)
  const [krok, setKrok] = useState("zavrete");
  const [novy, setNovy] = useState("");
  const [listok, setListok] = useState("");
  const [platnostMin, setPlatnostMin] = useState(15);
  const [chyba, setChyba] = useState("");
  const [hlaska, setHlaska] = useState("");
  const [pracuje, setPracuje] = useState(false);

  const zavri = () => {
    setKrok("zavrete");
    setNovy("");
    setListok("");
    setChyba("");
  };

  const posliKod = async (adresa) => {
    setPracuje(true);
    setChyba("");
    try {
      const { data } = await axios.post(PROFILE_EMAIL_CODE_URL, { email: adresa });
      setListok(data.listok);
      setPlatnostMin(data.platnostMin ?? 15);
      setKrok("kod");
      return true;
    } catch (err) {
      setChyba(err.response?.data?.message || "Kód sa nepodarilo odoslať.");
      return false;
    } finally {
      setPracuje(false);
    }
  };

  const odosliAdresu = async (e) => {
    e.preventDefault();
    const adresa = novy.trim().toLowerCase();
    if (!adresa) {
      setChyba("Zadajte e-mail.");
      return;
    }
    setNovy(adresa);
    await posliKod(adresa);
  };

  const potvrdKod = async (kod) => {
    setPracuje(true);
    setChyba("");
    try {
      const { data } = await axios.post(PROFILE_EMAIL_CONFIRM_URL, {
        email: novy,
        emailListok: listok,
        emailKod: kod,
      });
      setHlaska(
        data.zmenenePrihlasovacieMeno
          ? "E-mail je overený. Nabudúce sa prihlasujte týmto e-mailom."
          : "E-mail je overený a uložený.",
      );
      zavri();
      onZmenene?.(data.email);
    } catch (err) {
      setChyba(err.response?.data?.message || "Kód sa nepodarilo overiť.");
    } finally {
      setPracuje(false);
    }
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title>E-mail</Card.Title>
      </Card.Header>
      <Card.Body>
        {krok === "kod" ? (
          <EmailCodeStep
            email={novy}
            onSubmit={potvrdKod}
            onResend={() => posliKod(novy)}
            onBack={() => setKrok("adresa")}
            submitLabel="Potvrdiť e-mail"
            chyba={chyba}
            odosiela={pracuje}
            platnostMin={platnostMin}
          />
        ) : krok === "adresa" ? (
          // Uprava ma rovnaku stavbu ako zobrazenie (pole, poznamka, tlacidla),
          // len pole sa z textu zmeni na vstup -- karta pri kliknuti neposkoci.
          <form className="profile__form" onSubmit={odosliAdresu}>
            <div className="profile__form-field">
              <label className="profile__form-label" htmlFor="profil-novy-email">
                Nový e-mail
              </label>
              <input
                id="profil-novy-email"
                type="email"
                className="profile__form-input"
                value={novy}
                onChange={(e) => setNovy(e.target.value)}
                placeholder={email || "meno@example.sk"}
                autoComplete="email"
                autoFocus
              />
            </div>
            <p className="profile__form-hint">
              Overovací kód pošleme na túto adresu. E-mail sa uloží až po jeho zadaní.
            </p>
            {chyba && <p className="profile__form-error">{chyba}</p>}
            <div className="profile__form-actions">
              <Button type="button" variant="outline" onClick={zavri} disabled={pracuje}>
                Zrušiť
              </Button>
              <Button type="submit" variant="primary" disabled={pracuje}>
                {pracuje ? "Posielam kód..." : "Poslať overovací kód"}
              </Button>
            </div>
          </form>
        ) : (
          <div className="profile__form">
            {email ? (
              <>
                <div className="profile__form-field">
                  <span className="profile__form-label">Aktuálny e-mail</span>
                  <p className="profile__email-hodnota">
                    <Mail size={16} aria-hidden="true" />
                    <span>{email}</span>
                  </p>
                </div>
                <p className="profile__form-hint">
                  Na túto adresu vám pošleme kód, keď zabudnete heslo.
                </p>
              </>
            ) : (
              <p className="profile__email-missing" role="status">
                <AlertTriangle size={17} aria-hidden="true" />
                Nemáte zadaný e-mail. Bez neho si nedokážete obnoviť zabudnuté heslo — doplňte
                si ho, prosím.
              </p>
            )}

            {hlaska && <p className="profile__form-success">{hlaska}</p>}

            <div className="profile__form-actions">
              <Button
                type="button"
                variant={email ? "outline" : "primary"}
                onClick={() => {
                  setHlaska("");
                  setChyba("");
                  setKrok("adresa");
                }}
              >
                {email ? "Zmeniť e-mail" : "Doplniť e-mail"}
              </Button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default EmailKarta;
